import { pdf } from '@react-pdf/renderer';
import PDFMerger from 'pdf-merger-js';
import React from 'react';

import contentWithFCRAPdf from '../assets/pdfs/content-with-fcra.pdf';
import contentWithoutFCRAPdf from '../assets/pdfs/content-without-fcra.pdf';
import { LetterTypes } from '../types';
import { getLetterFileName } from '../utils';

export interface MergeLetterPdfOptions {
  hasFcra: boolean;
  openInNewTab: boolean;
  policyNumber: string;
  letterType: LetterTypes;
}

// Single source of truth for letter PDF generation:
// 1. render the dynamic body React element to a blob
// 2. fetch the static FCRA / non-FCRA template
// 3. merge them with pdf-merger-js
// 4. optionally open in a new tab (legacy modal preview)
export async function mergeLetterPdf(
  dynamicElement: React.ReactElement,
  options: MergeLetterPdfOptions,
): Promise<File[]> {
  const dynamicPdfBlob = await pdf(dynamicElement).toBlob();
  const dynamicPdfFile = new File([dynamicPdfBlob], 'dynamic.pdf', {
    type: 'application/pdf',
  });

  const templatePath = options.hasFcra ? contentWithFCRAPdf : contentWithoutFCRAPdf;
  const response = await fetch(templatePath);
  if (!response.ok) throw new Error(`Failed to load PDF: ${templatePath}`);
  const templatePdfBlob = await response.blob();
  const templatePdfFile = new File([templatePdfBlob], 'template.pdf', {
    type: 'application/pdf',
  });

  const merger = new PDFMerger();
  await merger.add(dynamicPdfFile);
  await merger.add(templatePdfFile);
  const mergedPdfBuffer = await merger.saveAsBuffer();
  const mergedPdfBlob = new Blob([mergedPdfBuffer], { type: 'application/pdf' });

  if (options.openInNewTab) {
    const blobUrl = URL.createObjectURL(mergedPdfBlob);
    window.open(blobUrl, '_blank');
  }

  const fileName = `${options.policyNumber}_${getLetterFileName(options.letterType)}_letter.pdf`;
  return [new File([mergedPdfBlob], fileName, { type: 'application/pdf' })];
}
